import { TextState } from '@/types'
import { ComponentPublicInstance, CSSProperties } from 'vue'
import { DisplayObjectPair } from '@/pixi/types'
import html2canvas from 'html2canvas'
import * as PIXI from 'pixi.js'

export interface TextImplementation {
  state: TextState
  // This element is used as a reference to rasterise text using html2canvas
  renderTextReferenceElement: HTMLElement | null
  // This used to keep track of the width of the rasterised text
  rasterisedTextWidth: number

  /** This property is used to retain the editable text until the text has rasterised and we can swap it out */
  isTextRasterised: boolean

  /** This getter returns the styling for the wrapper around the text controls. It mimics the rendered text location and width */
  readonly canvasTextContainerStyle: CSSProperties

  setRenderTextReferenceElement(el: Element | ComponentPublicInstance | null): void
}

export default function useTextCanvas () {
  /**
   * Initialises a text implementation object from a text state object.
   */
  const createTextImplementation = (state: TextState): TextImplementation => {
    return {
      state,
      renderTextReferenceElement: null,
      rasterisedTextWidth: 0,
      setRenderTextReferenceElement (el: Element | ComponentPublicInstance | null) {
        this.renderTextReferenceElement = el as HTMLElement | null
      },
      isTextRasterised: false,
      get canvasTextContainerStyle (): CSSProperties {
        return {
          transform: `translate(${this.state.x}px, ${this.state.y}px)`,
          transformOrigin: 'top left',
          position: 'absolute',
          left: 0,
          top: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'calc(8px / var(--canvas-scale))',
          width: this.rasterisedTextWidth * this.state.scale + 'px'
        }
      }
    }
  }

  const rasterizeText = async (
    textImplementation: TextImplementation,
    textDisplayObjectsMap: Map<number, DisplayObjectPair>
  ): Promise<void> => {
    if (textDisplayObjectsMap.has(textImplementation.state.id) && textImplementation.renderTextReferenceElement) {
      const { sprite } = textDisplayObjectsMap.get(textImplementation.state.id) as DisplayObjectPair
      // If editing mode is disabled, we need to rasterize the text into an image
      const screenshotCanvas = await (
        html2canvas(textImplementation.renderTextReferenceElement, {
          backgroundColor: 'transparent',
          // Ignore specific tag elements to suppress warning about cloning canvas (we do not need it)
          ignoreElements: (element: Element) => ['VIDEO', 'PICTURE', 'IMG', 'CANVAS'].includes(element.tagName),
          scale: 1
        })
      )
      sprite.texture = PIXI.Texture.from(screenshotCanvas)
      sprite.position.set(textImplementation.state.x, textImplementation.state.y)
      sprite.scale.set(textImplementation.state.scale)

      // Update the rasterized text width
      textImplementation.rasterisedTextWidth = sprite.texture.width
    }
  }

  return {
    createTextImplementation,
    rasterizeText
  }
}
